import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { CONSENT_TEXT } from "../lib/content";
import { getAttribution } from "../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNEYS = ["Just starting to explore", "Actively looking at homes", "Found a home I want", "Renting and curious what I qualify for"];
const STATES = ["Nevada", "Texas", "Other"];

export const RegistrationForm = ({ idPrefix }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", state: "", journey: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please check the consent box to register.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/ghl-lead`, {
        ...form,
        consentText: CONSENT_TEXT,
        consentTimestamp: new Date().toISOString(),
        ...getAttribution(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="glass-panel p-8 text-center" data-testid={`${idPrefix}-success-card`}>
        <CheckCircle2 className="mx-auto mb-4" size={44} style={{ color: "#7C3AED" }} />
        <h3 className="font-display font-semibold text-xl uppercase tracking-tight mb-2">Your Seat Is Reserved</h3>
        <p className="text-sm text-black/55 leading-relaxed">
          Check your email for your session access details, your Pre-Approval Document Checklist, and your program guide. See you September 17th at 6 PM PDT.
        </p>
        <p className="font-mono-c text-[10px] tracking-[0.2em] text-black/30 mt-5">FREE EVENT · NO OBLIGATION · NO HARD CREDIT PULL</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${idPrefix}-registration-form`}>
      <div className="grid grid-cols-2 gap-3">
        <input required className="form-input" placeholder="First name" value={form.firstName} onChange={set("firstName")} data-testid={`${idPrefix}-input-first-name`} />
        <input required className="form-input" placeholder="Last name" value={form.lastName} onChange={set("lastName")} data-testid={`${idPrefix}-input-last-name`} />
      </div>
      <input required type="email" className="form-input" placeholder="Email address" value={form.email} onChange={set("email")} data-testid={`${idPrefix}-input-email`} />
      <input required type="tel" className="form-input" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} data-testid={`${idPrefix}-input-phone`} />
      <div className="grid grid-cols-2 gap-3">
        <select required className="form-input" value={form.state} onChange={set("state")} data-testid={`${idPrefix}-select-state`}>
          <option value="" disabled>State</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-input" value={form.journey} onChange={set("journey")} data-testid={`${idPrefix}-select-journey`}>
          <option value="">Journey (optional)</option>
          {JOURNEYS.map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
      </div>
      <label className="flex items-start gap-2.5 pt-1">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 accent-[#7C3AED]" data-testid={`${idPrefix}-consent-checkbox`} />
        <span className="text-[10px] leading-relaxed text-black/35">{CONSENT_TEXT}</span>
      </label>
      <button type="submit" disabled={loading} className="btn-accent w-full py-3.5 text-[13px] disabled:opacity-60" data-testid={`${idPrefix}-submit-button`}>
        {loading ? "RESERVING…" : "✓ RESERVE MY FREE SEAT"}
      </button>
      <p className="font-mono-c text-[10px] text-black/25 text-center tracking-wide">
        Free educational event · No obligation · No hard credit pull required to attend
      </p>
    </form>
  );
};
